// singletons
import { Application } from "Application";
import {GameState} from "Models/GameState";

// library functions
import { Elastic, Expo } from "gsap/EasePack";
import { TimelineLite } from "gsap/TimelineLite";
import { TimelineMax } from "gsap/TimelineMax";
import { TweenLite } from "gsap/TweenLite";

import { makeSprite } from "html-living-framework/lib/Utils/PixiUtils";

// pubsub topics
import { ALL_ITEMS_RETURNED, ITEM_RETURNED } from "Data/Topics";
import { UNREGISTER_FOCUSABLE } from "html-living-framework/lib/Data/Topics";

// gameobjects
import { InteriorItemIconReturnItems } from "GameObjects/GameInterior/ReturnItems/InteriorItemIconReturnItems";
import { Receptacle } from "html-living-framework/lib/Input/Receptacle";

// types and interfaces
import type { BGConfig } from "Data/BGConfig";
import { GameConfig } from "Data/GameConfig";
import type { ItemID, LocationID } from "Data/GameConfig";
import { MusicNoteParticleSystem } from "GameObjects/GameInterior/ReturnItems/MusicNoteParticleSystem";
import { SFX } from "html-living-framework/lib/Audio/AudioCategory";
import type { Draggable } from "html-living-framework/lib/Input/Draggable";
import { ArrayUtils } from "html-living-framework/lib/Utils";
import type { IItemReturnSlotConfig } from "Interfaces/IItemReturnSlotConfig";
import type { IItemState } from "Interfaces/IItemState";
import type { ILevelState } from "Interfaces/ILevelState";

export class InteriorItemReceptacle extends Receptacle {
    public bg?: PIXI.Container;

    protected itemID?: ItemID;
    protected icon?: InteriorItemIconReturnItems;
    protected acceptsMultiple: boolean;
    protected keepItemIcons: boolean;
    protected hideIconImmediately: boolean;
    protected icons: InteriorItemIconReturnItems[] = [];
    protected _width: number;
    protected _height: number;
    protected animateOnDrop: "squash" | "none" = "none";
    protected scaleUpOnDragOver: boolean;
    protected _dropSFX: string | string[] | undefined;
    protected musicnotes: boolean; // HACK

    constructor(config: IItemReturnSlotConfig, locationID: LocationID) {
        super();

        this.acceptsMultiple = config.acceptsMultiple ?? false;
        this.keepItemIcons = config.keepItemIcons ?? false;
        this.hideIconImmediately = config.hideIconImmediately ?? false;
        this.animateOnDrop = config.animateOnDrop ?? "none";
        this.scaleUpOnDragOver = config.scaleUpOnDragOver ?? false;
        this.musicnotes = config.musicnotes ?? false;

        this._width = config.width;
        this._height = config.height;
        this._dropSFX = config.dropSFX;

        if (this.acceptsMultiple) {
            // top left anchor
            this.hitArea = new PIXI.Rectangle(-config.width / 2, -config.height / 2, config.width, config.height);
        }
        else {
            // center anchor
            this.hitArea = new PIXI.Rectangle(0, 0, config.width, config.height);
        }

        if (config.bg !== undefined) {
            this.addBG(config.bg, locationID);
        }

        this._subscriptions.push(
            PubSub.subscribe(ITEM_RETURNED, this.onMessageReceived.bind(this)),
            PubSub.subscribe(ALL_ITEMS_RETURNED, this.onMessageReceived.bind(this)),
        );
    }

    protected onPointerOver() {
        super.onPointerOver();
    }

    protected onPointerOut() {
        super.onPointerOut();
    }

    protected onDragBegin(pTopic: string, pDraggable: Draggable): void {
        super.onDragBegin(pTopic, pDraggable);
        if (this.interactive && this.scaleUpOnDragOver) {
            TweenLite.to(this, 0.3, {pixi: {scale: 1.1}});
        }
    }

    protected onDragEnd(pTopic: string, pDraggable: Draggable): void {
        super.onDragEnd(pTopic, pDraggable);
        if (!this.interactive && this.scaleUpOnDragOver) {
            TweenLite.to(this, 0.3, {pixi: {scale: 1}});
        }
    }

    protected onDraggableSelected(pTopic: string, pDraggable: Draggable): void {
        super.onDraggableSelected(pTopic, pDraggable);
        if (this.interactive && this.scaleUpOnDragOver) {
            TweenLite.to(this, 0.3, {pixi: {scale: 1.1}});
        }
    }

    protected onDraggableDeselected(pTopic: string, pDraggable: Draggable): void {
        super.onDraggableDeselected(pTopic, pDraggable);
        if (!this.interactive && this.scaleUpOnDragOver) {
            TweenLite.to(this, 0.3, {pixi: {scale: 1}});
        }
    }

    protected addDraggable(pDraggable: Draggable): void {
        if (pDraggable instanceof InteriorItemIconReturnItems) {
            this.icon = pDraggable;
            pDraggable.deselect();
            this.itemID = pDraggable.id;
            GameState.Instance.ReturnItem(pDraggable.id);
        }
    }

    protected onMessageReceived(pTopic: string, pData: any) {
        switch (pTopic) {
            case ITEM_RETURNED:
                this.OnItemReturned(pData as IItemState);
                break;
            case ALL_ITEMS_RETURNED:
                this.OnAllItemsReturned(pData as ILevelState);
                break;
        }
    }

    protected OnItemReturned(state: IItemState) {
        if (state.id === this.itemID) {
            this.icons.push(this.icon!);

            if (!this.acceptsMultiple) {
                if (this._dropSFX !== undefined) {
                    if (Array.isArray(this._dropSFX)) {
                        Application.instance.audio.play(ArrayUtils.random(this._dropSFX), 1, false, SFX);
                    }
                    else {
                        Application.instance.audio.play(this._dropSFX, 1, false, SFX);
                    }
                }

                if (this.icon !== undefined) {
                    this.icon.position.set(
                        this.position.x + this._width / 2,
                        this.position.y + this._height / 2,
                    );
                    this.isActive = false;
                    PubSub.publishSync(UNREGISTER_FOCUSABLE, this);
                }
            }
            else {
                const numIcons = this.icons.length;

                if (this._dropSFX !== undefined) {
                    if (Array.isArray(this._dropSFX)) {
                        Application.instance.audio.play(this._dropSFX[numIcons % this._dropSFX.length], 1, false, SFX);
                    }
                    else {
                        Application.instance.audio.play(this._dropSFX, 1, false, SFX);
                    }
                }
                if (this.hideIconImmediately) {
                    this.icon!.visible = false;
                }
                else {
                    this.icons.forEach((e, i) => {
                        const x = (i - (numIcons / 2) + 0.5) * Math.min((this._width / numIcons), 100);
                        if (e === this.icon) {
                            e.position.x = this.position.x + x;
                            e.position.y = this.position.y;
                            e.scale.set(0.8);
                        }
                        else {
                            TweenLite.killTweensOf(e);
                            TweenLite.to(e, 0.05, {pixi: {
                                positionX: this.position.x + x,
                            }});
                        }
                    });
                }
            }

            if (this.animateOnDrop === "squash") {
                if (this.bg !== undefined) {
                    new TimelineLite()
                        // squash
                        .to(this.bg.children, 5 / 30, {pixi: {scaleX: 1.1, scaleY: 0.8}})
                        // stretch
                        .to(
                            this.bg.children,
                            1,
                            { pixi: {scaleX: 1, scaleY: 1}, ease: Elastic.easeOut },
                        )
                    ;
                }
            }
        }
    }

    protected OnAllItemsReturned(state: ILevelState) {
        PubSub.publishSync(UNREGISTER_FOCUSABLE, this);
        if (this.acceptsMultiple && !this.keepItemIcons) {
            this.icons.forEach((i) => i.visible = false);
        }
        if (this.musicnotes) {
            const particles = new MusicNoteParticleSystem();
            this.addChild(particles);

            const loop = new TimelineMax();
            loop.repeat(8);
            loop
                .call(() => {
                    particles.startEmitting();
                })
                .to(this.bg!.children, 0.5, { // HACK
                    pixi: {
                        scaleY: 0.9,
                        scaleX: 1.1,
                    },
                    ease: Expo.easeOut,
                }).to(this.bg!.children, 0.5, { // HACK
                    pixi: {
                        scaleX: 1,
                        scaleY: 1,
                    },
                    ease: Expo.easeIn,
                })
                .delay(1 + (5 / 30));
        }
    }

    private addBG(bg: BGConfig, id: LocationID) {
        if (Array.isArray(bg)) {
            this.bg = this.addChild(new PIXI.Container());
            for (const layer of bg) {
                const newSprite = makeSprite(id + "/" + layer.filename, GameConfig.spritesheets[id]);
                newSprite.position.x = layer.x;
                newSprite.position.y = layer.y + (newSprite.height / 2);
                this.bg.addChild(newSprite);
                newSprite.anchor.set(0.5, 1);
            }
        }
        else {
            this.bg = makeSprite(id + "/" + bg.filename, GameConfig.spritesheets[id]);
            this.addChild(this.bg);
            (this.bg as PIXI.Sprite).anchor.set(0.5, 1);
            this.bg.position.set(0, this.bg.height / 2);
        }
    }
}
